import React, { CSSProperties, ReactNode } from "react";

interface FlexNavbarExportProps {
  children?: ReactNode;
  backgroundColor?: string;
  textColor?: string;
  sticky?: boolean;
  maxWidth?: string;
  paddingX?: number;
  paddingY?: number;
  gap?: number;
  mobileBreakpoint?: number;
}

export const FlexNavbarExport: React.FC<FlexNavbarExportProps> = ({
  children,
  backgroundColor = "#ffffff",
  textColor = "#1f2937",
  sticky = true,
  maxWidth = "1280px",
  paddingX = 24,
  paddingY = 12,
  gap = 16,
}) => {
  const childArray = React.Children.toArray(children);
  const logoChild = childArray[0]; // First child = Logo
  const menuChildren = childArray.slice(1); // Rest = Menu items

  const navStyle: CSSProperties = {
    width: "100%",
    backgroundColor,
    color: textColor,
    position: sticky ? "sticky" : "relative",
    top: sticky ? 0 : "auto",
    zIndex: 50,
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  };

  const innerStyle: CSSProperties = {
    maxWidth: maxWidth,
    margin: "0 auto",
    paddingTop: `${paddingY}px`,
    paddingBottom: `${paddingY}px`,
    paddingLeft: `${paddingX}px`,
    paddingRight: `${paddingX}px`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: `${gap}px`,
    minHeight: "56px",
  };

  const desktopMenuStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: `${gap}px`,
  };

  return (
    <nav className="navbar flex-navbar" style={navStyle}>
      <div className="navbar-inner flex-navbar-inner" style={innerStyle}>
        {/* Logo */}
        {logoChild && <div className="navbar-logo flex-navbar-logo">{logoChild}</div>}

        {/* Desktop Menu - pushed to right with marginLeft auto */}
        {menuChildren.length > 0 && (
          <div className="navbar-desktop flex-navbar-desktop" style={{ ...desktopMenuStyle, flexShrink: 0, marginLeft: "auto" }}>
            {menuChildren}
          </div>
        )}

        {/* Mobile Hamburger Button */}
        {menuChildren.length > 0 && (
          <button
            className="navbar-mobile-btn flex-navbar-mobile-btn"
            style={{
              display: "none",
              color: textColor,
              backgroundColor: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "8px",
            }}
            aria-label="Open menu"
          >
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        )}
      </div>

      {/* Mobile Drawer */}
      {menuChildren.length > 0 && (
        <div
          className="mobile-drawer flex-navbar-drawer"
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            width: "80vw",
            maxWidth: "300px",
            height: "100vh",
            backgroundColor,
            boxShadow: "-2px 0 8px rgba(0,0,0,0.15)",
            zIndex: 999,
            padding: "16px",
            display: "flex",
            flexDirection: "column",
            transform: "translateX(100%)",
            transition: "transform 0.3s ease",
          }}
        >
          <button
            className="mobile-drawer-close"
            style={{
              alignSelf: "flex-end",
              color: textColor,
              backgroundColor: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "8px",
              marginBottom: "16px",
            }}
            aria-label="Close menu"
          >
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>{menuChildren}</div>
        </div>
      )}

      {/* Mobile Overlay */}
      <div
        className="mobile-overlay flex-navbar-overlay"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          zIndex: 998,
        }}
      />
    </nav>
  );
};
